// A DATE-ONLY task (Pouria, 2026-09-22): "just pick a day for a task, without a time".
//
// The contract, shared with busy-task-srv / busy-main-bff (TaskDbModel.IsAllDay):
//   • `isAllDay: true` and `dueDate` = that calendar day at UTC MIDNIGHT ("2026-09-24T00:00:00Z").
//     The date part is the fact; the clock is nothing.
//   • On the way IN (store fetch) the day is re-anchored to LOCAL midnight, so every `new Date(dueDate)`
//     in the app lands on the right day for the viewer — the store's own timezone shift (task.js) must
//     not touch it, or a viewer west of UTC would see the day before.
//   • A date-only task is DUE at the END of its day, not at 00:00: it is overdue only once the day is
//     over, and never "due in 3 hours" at 9 pm the evening before.
//
// This file is the one place those three rules are written; the readers below import from here.

/** True when the entity is a date-only task. */
export const isDateOnly = (entity) => !!entity?.isAllDay

/** The calendar day of a date-only task as a LOCAL midnight Date, from the stored UTC-midnight ISO. */
export const dateOnlyDay = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

/** The stored form of a date-only due date: the LOCAL calendar day of `d`, at UTC midnight, as ISO. */
export const dateOnlyIso = (d) => {
  const day = d instanceof Date ? d : new Date(d)
  if (isNaN(day.getTime())) return null
  return new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate())).toISOString()
}

/** The last millisecond of the local day `d` falls on. */
export const endOfDay = (d) => {
  const day = d instanceof Date ? d : new Date(d)
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999)
}

/**
 * The instant a task's due date is COMPARED at — overdue, due soon, sorting into "today". A timed
 * task is its own instant; a date-only task is the end of its day.
 */
export const taskDueInstant = (entity) => {
  if (!entity?.dueDate) return null
  const d = new Date(entity.dueDate)
  if (isNaN(d.getTime())) return null
  return isDateOnly(entity) ? endOfDay(d) : d
}
